// /opt/isp_erp/frontend/src/pages/admin/finanzas/PagosList.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../../api/axios';
import { pagosApi } from '../../../api/finanzas/pagos.api';
import { facturasApi } from '../../../api/finanzas/facturas.api';
import { finanzasAPI } from '../../../api/finanzas/finanzas.api';
import './PagosList.css';

export default function PagosList() {
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [metodoPago, setMetodoPago] = useState('EFECTIVO');
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cuentaBancariaId, setCuentaBancariaId] = useState('');
  const [puntosCobro, setPuntosCobro] = useState([]);
  const [puntoCobroId, setPuntoCobroId] = useState('');

  useEffect(() => {
    cargarClientesConDeudas();
    cargarCuentasBancarias();
    cargarPuntosCobro();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setClientesFiltrados([]);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtrados = clientes.filter(c =>
        c.nombres?.toLowerCase().includes(term) ||
        c.apellidos?.toLowerCase().includes(term) ||
        c.identificacion?.includes(term)
      );
      setClientesFiltrados(filtrados);
    }
  }, [searchTerm, clientes]);

  const cargarClientesConDeudas = async () => {
    setLoading(true);
    try {
      const response = await pagosApi.getClientesConDeudas();
      setClientes(response.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
      await cargarClientesConDeudasFallback();
    } finally {
      setLoading(false);
    }
  };

  const cargarCuentasBancarias = async () => {
    try {
      const response = await finanzasAPI.getCuentasBancarias();
      setCuentasBancarias(response.data);
    } catch (error) {
      console.error('Error cargando cuentas bancarias:', error);
    }
  };

  const cargarPuntosCobro = async () => {
    try {
      const response = await finanzasAPI.getPuntosCobro();
      setPuntosCobro(response.data);
      if (response.data.length > 0) {
        setPuntoCobroId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error cargando puntos de cobro:', error);
    }
  };

  const cargarClientesConDeudasFallback = async () => {
    try {
      const response = await axios.get('/customers?estado=ACTIVO,SUSPENDIDO');
      const todosClientes = response.data;
      const clientesConDeudas = [];

      for (const cliente of todosClientes) {
        try {
          const facturas = await facturasApi.listar({
            customer_id: cliente.id,
            estado: 'PENDIENTE,PARCIAL',
            limit: 1
          });

          if (facturas.data.length > 0) {
            const resumen = await facturasApi.resumenCliente(cliente.id);
            clientesConDeudas.push({
              ...cliente,
              saldo_pendiente: resumen.data.saldo_pendiente,
              suspendido: cliente.estado === 'SUSPENDIDO',
              deuda_total: resumen.data.saldo_pendiente
            });
          }
        } catch (error) {
          console.error(`Error verificando cliente ${cliente.id}:`, error);
        }
      }
      setClientes(clientesConDeudas);
    } catch (error) {
      console.error('Error cargando clientes fallback:', error);
    }
  };

  const seleccionarCliente = async (cliente) => {
    setClienteSeleccionado(cliente);
    setSearchTerm('');
    setClientesFiltrados([]);
    setMonto('');
    setFacturaSeleccionada(null);
    setCuentaBancariaId('');
    setPuntoCobroId(puntosCobro.length > 0 ? puntosCobro[0].id : '');

    try {
      const response = await facturasApi.listar({
        customer_id: cliente.id,
        estado: ['PENDIENTE', 'PARCIAL'],
        limit: 100
      });
      setFacturasPendientes(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error cargando facturas:', error);
      alert('Error al cargar las facturas del cliente');
    }
  };

  const handleRegistrarPago = async () => {
    if (!monto || monto <= 0) {
      alert('Ingrese un monto válido');
      return;
    }

    if (!puntoCobroId) {
      alert('Debe seleccionar un punto de cobro');
      return;
    }

    setProcesando(true);
    try {
      const payload = {
        customer_id: clienteSeleccionado.id,
        factura_id: facturaSeleccionada?.id || null,
        monto: parseFloat(monto),
        metodo: metodoPago,
        referencia: referencia || null,
        notas: notas || null,
        punto_cobro_id: puntoCobroId,
        cuenta_bancaria_id: metodoPago === 'TRANSFERENCIA' ? (cuentaBancariaId || null) : null
      };

      await pagosApi.crear(payload);
      setShowModal(false);
      alert('✅ Pago registrado correctamente');
      cargarClientesConDeudas();
    } catch (error) {
      console.error('Error registrando pago:', error);
      alert(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setProcesando(false);
    }
  };

  const metodosPago = [
    { value: 'EFECTIVO', label: '💵 Efectivo' },
    { value: 'TRANSFERENCIA', label: '🏦 Transferencia' },
    { value: 'TARJETA_CREDITO', label: '💳 Tarjeta Crédito' },
    { value: 'TARJETA_DEBITO', label: '💳 Tarjeta Débito' },
    { value: 'DEPOSITO', label: '🏧 Depósito' },
    { value: 'CHEQUE', label: '📝 Cheque' }
  ];

  if (loading) {
    return (
      <div className="pagos-loading-container">
        <div className="pagos-loading-spinner"></div>
        <p>Buscando clientes con deudas...</p>
      </div>
    );
  }

  return (
    <div className="pagos-container">
      {/* Header */}
      <div className="pagos-header">
        <div className="pagos-header-left">
          <div className="pagos-header-icon">💰</div>
          <div>
            <h1>Cobros y Promesas de Pago</h1>
            <p>Busque un cliente para registrar su pago</p>
          </div>
        </div>
        <div className="pagos-stats">
          <span className="stats-badge">
            {clientes.length} clientes con deudas
          </span>
        </div>
      </div>

      {/* Buscador */}
      <div className="pagos-search-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="Buscar cliente por nombre o identificación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            autoFocus
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="search-clear"
              title="Limpiar búsqueda"
            >
              ✕
            </button>
          )}
        </div>
        <p className="search-hint">
          Escriba al menos 3 caracteres para buscar
        </p>
      </div>

      {/* Resultados */}
      {searchTerm && (
        <div className="pagos-results">
          {clientesFiltrados.length === 0 ? (
            <div className="results-empty">
              <div className="empty-icon">🔍</div>
              <p className="empty-title">No se encontraron clientes con deudas</p>
              <p className="empty-subtitle">Pruebe con otros términos de búsqueda</p>
            </div>
          ) : (
            <ul className="results-list">
              {clientesFiltrados.map(cliente => (
                <li
                  key={cliente.id}
                  onClick={() => seleccionarCliente(cliente)}
                  className={`result-item ${cliente.suspendido ? 'result-item-suspendido' : ''}`}
                >
                  <div className="result-info">
                    <div className="result-avatar">
                      {cliente.nombres?.charAt(0) || '👤'}
                    </div>
                    <div className="result-details">
                      <p className="result-name">
                        {cliente.nombres} {cliente.apellidos}
                        {cliente.suspendido && (
                          <span className="status-badge status-suspendido">⛔ SUSPENDIDO</span>
                        )}
                      </p>
                      <div className="result-meta">
                        <span className="meta-id">ID: {cliente.identificacion}</span>
                        {cliente.telefono && (
                          <span className="meta-phone">📞 {cliente.telefono}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="result-deuda">
                    <p className="deuda-monto">${(cliente.deuda_total || cliente.saldo_pendiente || 0).toFixed(2)}</p>
                    <p className="deuda-label">deuda total</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Mensaje si no hay clientes */}
      {clientes.length === 0 && !loading && !searchTerm && (
        <div className="pagos-success">
          <div className="success-icon">🎉</div>
          <p className="success-title">¡No hay clientes con deudas!</p>
          <p className="success-subtitle">Todos los clientes están al día</p>
        </div>
      )}

      {/* Modal de pago */}
      {showModal && clienteSeleccionado && (
        <div className="pagos-modal-overlay">
          <div className="pagos-modal-content">
            <div className="pagos-modal-header">
              <div className="modal-header-icon">💰</div>
              <div>
                <h3>Registrar Cobro</h3>
                <p>Complete los detalles del pago</p>
              </div>
            </div>

            <div className="pagos-modal-body">
              {/* Info del cliente */}
              <div className="cliente-info-card">
                <div className="cliente-avatar">
                  {clienteSeleccionado.nombres?.charAt(0)}
                </div>
                <div className="cliente-details">
                  <p className="cliente-nombre">
                    {clienteSeleccionado.nombres} {clienteSeleccionado.apellidos}
                    {clienteSeleccionado.suspendido && (
                      <span className="status-badge status-suspendido" style={{ marginLeft: '10px' }}>⛔ SUSPENDIDO</span>
                    )}
                  </p>
                  <p className="cliente-id">ID: {clienteSeleccionado.identificacion}</p>
                </div>
              </div>

              {/* Advertencia si está suspendido */}
              {clienteSeleccionado.suspendido && (
                <div className="alert-warning-modal">
                  <span className="alert-icon">⚠️</span>
                  <div className="alert-content">
                    <strong>Cliente suspendido</strong>
                    <p>Este cliente tiene el servicio suspendido por falta de pago. Al registrar el pago, se reactivará automáticamente.</p>
                  </div>
                </div>
              )}

              {/* Facturas pendientes */}
              {facturasPendientes.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Factura a pagar</label>
                  <select
                    className="form-select"
                    onChange={(e) => {
                      if (e.target.value) {
                        const factura = facturasPendientes.find(f => f.id === parseInt(e.target.value));
                        setFacturaSeleccionada(factura);
                        setMonto(factura.saldo_pendiente);
                      } else {
                        setFacturaSeleccionada(null);
                      }
                    }}
                    value={facturaSeleccionada?.id || ''}
                  >
                    <option value="">Pago sin factura específica</option>
                    {facturasPendientes.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.numero_factura} - ${f.saldo_pendiente?.toFixed(2)}
                        (Vence: {new Date(f.fecha_vencimiento).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Monto */}
              <div className="form-group">
                <label className="form-label required">
                  Monto a pagar ($) <span className="required-star">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  className="form-input"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Método de pago */}
              <div className="form-group">
                <label className="form-label">Método de pago</label>
                <select
                  value={metodoPago}
                  onChange={(e) => setMetodoPago(e.target.value)}
                  className="form-select"
                >
                  {metodosPago.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Punto de Cobro */}
              <div className="form-group">
                <label className="form-label">Punto de Cobro</label>
                <select
                  value={puntoCobroId}
                  onChange={(e) => setPuntoCobroId(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Seleccione un punto de cobro</option>
                  {puntosCobro.map(punto => (
                    <option key={punto.id} value={punto.id}>
                      {punto.nombre} - {punto.sucursal?.nombre || punto.sucursal_nombre || 'Sin sucursal'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cuenta bancaria (solo para transferencias) */}
              {metodoPago === 'TRANSFERENCIA' && (
                <div className="form-group">
                  <label className="form-label">Cuenta Bancaria Destino</label>
                  <select
                    value={cuentaBancariaId}
                    onChange={(e) => setCuentaBancariaId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Seleccione una cuenta</option>
                    {cuentasBancarias.map(cuenta => (
                      <option key={cuenta.id} value={cuenta.id}>
                        {cuenta.banco} - {cuenta.numero_cuenta} ({cuenta.tipo_cuenta})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Referencia */}
              <div className="form-group">
                <label className="form-label">
                  Referencia <span className="optional">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  className="form-input"
                  placeholder="N° de transferencia, cheque, etc."
                />
              </div>

              {/* Notas */}
              <div className="form-group">
                <label className="form-label">
                  Notas <span className="optional">(opcional)</span>
                </label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  className="form-textarea"
                  rows="2"
                  placeholder="Observaciones adicionales"
                />
              </div>
            </div>

            {/* Botones */}
            <div className="pagos-modal-footer">
              <button
                onClick={() => {
                  setShowModal(false);
                  setClienteSeleccionado(null);
                }}
                className="pagos-btn-secondary"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleRegistrarPago}
                disabled={procesando || !monto || !puntoCobroId}
                className="pagos-btn-primary"
              >
                {procesando ? (
                  <>
                    <span className="loading-spinner-small"></span>
                    Procesando...
                  </>
                ) : (
                  '✅ Registrar Pago'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
